import React, { useState } from 'react';
import {
  BarChart3, Calendar, CalendarCheck, CheckCircle2, Clock, Coins, History, LayoutDashboard, Palmtree, Plus, ShieldCheck, TrendingDown, UserCheck, Users, XCircle,
} from 'lucide-react';
import { Kpi } from '../attendance/shared';
import LeaveRequestForm from './LeaveRequestForm';
import BalanceHub from './BalanceHub';
import LeaveCalendar from './LeaveCalendar';
import Monetization from './Monetization';
import Reports, { AuditTrail } from './Reports';
import Approval from './Approval';
import { InnerTabs, longDate } from './shared';

// Advanced Leave Management (Stephanet's leavemgmt/AdvancedDashboard.jsx): the whole leave lifecycle in one place — dashboard, request,
// approval, calendar, balance, monetization, reports and audit trail

const INNER = [['dash', 'Dashboard', LayoutDashboard], ['request', 'Leave Request', Plus], ['approval', 'Approval', ShieldCheck], ['calendar', 'Calendar', Calendar],
  ['balance', 'Balance', Palmtree], ['money', 'Monetization', Coins], ['reports', 'Reports', BarChart3], ['audit', 'Audit Trail', History]];

const List = ({ title, items, Ico, tone, empty = 'None' }) => (
  <div className={`mdr-card lvm-list t-${tone}`}>
    <div className="lvm-list-head"><Ico size={15} /> {title}<b>{items.length}</b></div>
    {items.length ? <ul>{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="muted">{empty}</p>}
  </div>
);

export default function AdvancedDashboard(props) {
  const [inner, setInner] = useState('dash');
  const { data } = props;
  const d = data.dashboard;
  const thisYear = data.today.slice(0, 4);
  const yearReqs = data.requests.filter(r => r.start_date.startsWith(thisYear));
  const waiting = d.pending_supervisor.length + d.pending_hr.length;
  return (
    <>
      <div className="mdr-card lvm-adv-head">
        <div className="mds-card-title"><LayoutDashboard size={17} /> Advanced Leave Management</div>
        <p>Complete leave lifecycle from policy setup to request, approval, attendance update, payroll posting, and audit trail.</p>
        <InnerTabs items={INNER} active={inner} onChange={setInner} />
      </div>

      {inner === 'dash' && (
        <>
          <div className="md-kpis mds-kpis lvm-kpis6">
            <Kpi label="Total Employees" value={d.employees} Ico={Users} tone="blue" note="Active staff" />
            <Kpi label="On Leave Today" value={d.on_leave_today.length} Ico={Palmtree} tone="teal" note={longDate(data.today)} i={1} />
            <Kpi label="Pending Requests" value={waiting} Ico={Clock} tone="amber" note="Supervisor + HR" i={2} />
            <Kpi label="Approved Leaves" value={yearReqs.filter(r => r.status === 'approved').length} Ico={CheckCircle2} tone="green" note={thisYear} i={3} />
            <Kpi label="Rejected Requests" value={yearReqs.filter(r => r.status === 'rejected').length} Ico={XCircle} tone="red" note={thisYear} i={4} />
            <Kpi label="Low Leave Balance" value={d.low_balance.length} Ico={TrendingDown} tone="orange" note="Annual ≤ 3 days" i={5} />
          </div>
          {waiting > 0 && (
            <div className="lvm-banner">
              <span><Clock size={15} /> <b>{waiting}</b> leave request(s) awaiting approval</span>
              <button className="mds-run sm" onClick={() => setInner('approval')}>Review</button>
            </div>
          )}
          <div className="lvm-lists">
            <List title="Employees Currently on Leave" items={d.on_leave_today} Ico={Palmtree} tone="teal" />
            <List title="Employees Resuming This Week" items={d.resuming.map(x => `${x.name} — ${longDate(x.date)}`)} Ico={UserCheck} tone="blue" />
            <List title="Pending Supervisor Approval" items={d.pending_supervisor} Ico={ShieldCheck} tone="amber" />
            <List title="Pending HR Final Approval" items={d.pending_hr} Ico={CalendarCheck} tone="purple" />
            <List title="Employees with Low Balance" items={d.low_balance.map(x => `${x.name} — ${x.days} day(s) annual leave left`)} Ico={TrendingDown} tone="orange" />
          </div>
        </>
      )}
      {inner === 'request' && <LeaveRequestForm {...props} />}
      {inner === 'approval' && <Approval {...props} />}
      {inner === 'calendar' && <LeaveCalendar {...props} />}
      {inner === 'balance' && <BalanceHub {...props} />}
      {inner === 'money' && <Monetization {...props} />}
      {inner === 'reports' && <Reports {...props} />}
      {inner === 'audit' && <AuditTrail {...props} />}
    </>
  );
}
